import React, { useState, useMemo, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import DateRangePicker from '../components/DateRangePicker';
import { usePartners } from '../context/PartnersContext';
import './Dashboard.css';

const Dashboard = () => {
  const { partners: partnersList, logsByPartner } = usePartners();
  const partnerNames = useMemo(() => partnersList.map((p) => p.name), [partnersList]);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    if (partnerNames.length && !partnerNames.includes(selectedPartner)) {
      setSelectedPartner(partnerNames[0]);
    } else if (partnerNames.length && !selectedPartner) {
      setSelectedPartner(partnerNames[0]);
    }
  }, [partnerNames, selectedPartner]);

  const formatDateRange = () => {
    if (startDate && endDate) {
      return `${startDate} → ${endDate}`;
    } else if (startDate) {
      return `${startDate} → Select end date`;
    }
    return 'Select date range';
  };

  const handleDateRangeClick = () => {
    setShowDatePicker(true);
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Filter logs based on selected partner and date range; sort by ID descending
  const filterLogs = () => {
    let logs = logsByPartner[selectedPartner] || [];

    if (startDate || endDate) {
      logs = logs.filter((log) => {
        const logDate = log.date;
        if (startDate && endDate) {
          return logDate >= startDate && logDate <= endDate;
        } else if (startDate) {
          return logDate >= startDate;
        } else if (endDate) {
          return logDate <= endDate;
        }
        return true;
      });
    }

    return [...logs].sort((a, b) => {
      const idA = (a.id || '').replace(/\D/g, '') || 0;
      const idB = (b.id || '').replace(/\D/g, '') || 0;
      if (idB !== idA) return Number(idB) - Number(idA);
      return (b.id || '').localeCompare(a.id || '', undefined, { numeric: true });
    });
  };

  const recentLogs = filterLogs();

  // Calculate stats based on filtered logs
  const calculateStats = () => {
    const successfulPayments = recentLogs.filter(log => 
      log.status === 'Success' && log.amount > 0
    );
    
    const totalTransactions = recentLogs.length;
    const totalMoney = successfulPayments.reduce((sum, log) => sum + log.amount, 0);
    const kycCompleted = recentLogs.filter(log => 
      log.action.includes('KYC') && log.status === 'Success'
    ).length;
    const complianceRejected = recentLogs.filter(log => 
      log.status === 'Failed' && log.action.includes('Compliance')
    ).length;
    
    // Get unique customers from logs
    const customerIds = new Set();
    recentLogs.forEach(log => {
      const match = log.details.match(/Customer ID: (CUST\d+)/);
      if (match) customerIds.add(match[1]);
    });

    return {
      customers: customerIds.size.toLocaleString(),
      transactions: totalTransactions.toLocaleString(),
      moneyTransferred: `$${totalMoney.toLocaleString()}`,
      kycCompleted: kycCompleted.toLocaleString(),
      complianceRejected: complianceRejected.toLocaleString(),
    };
  };

  const statsData = calculateStats();

  const stats = [
    { label: 'Customers', value: statsData.customers, icon: '👥' },
    { label: 'Transactions', value: statsData.transactions, icon: '💳' },
    { label: 'Money Transferred', value: statsData.moneyTransferred, icon: '💰' },
    { label: 'KYC Completed', value: statsData.kycCompleted, icon: '✅' },
    { label: 'Total Txn Rejected by Compliance', value: statsData.complianceRejected, icon: '❌' },
  ];

  return (
    <DashboardLayout>
      <div className="dashboard-page">
        <div className="dashboard-filters-top">
          <div className="filters-container">
            <div className="filter-group">
              <label>Partner</label>
              <select 
                className="filter-select"
                value={selectedPartner}
                onChange={(e) => setSelectedPartner(e.target.value)}
              >
                {partnerNames.map(partner => (
                  <option key={partner} value={partner}>{partner}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>Date Range</label>
              <div className="date-range-single" onClick={handleDateRangeClick}>
                <input
                  type="text"
                  className="date-range-input"
                  value={formatDateRange()}
                  readOnly
                  placeholder="Select date range"
                />
                <span className="calendar-icon">📅</span>
              </div>
            </div>
          </div>
        </div>

        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className="stat-header">
                <span className="stat-icon">{stat.icon}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
              <div className="stat-value">{stat.value}</div>
            </div>
          ))}
        </div>

        <div className="dashboard-sections">
          <div className="card main-card">
            <div className="card-header-section">
              <h2 className="card-title">Recent Logs ({recentLogs.length})</h2>
              <span className="partner-badge">{selectedPartner}</span>
            </div>
            
            <table className="logs-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>TIMESTAMP</th>
                  <th>ACTION</th>
                  <th>PARTNER</th>
                  <th>STATUS</th>
                  <th>DETAILS</th>
                </tr>
              </thead>
              <tbody>
                {recentLogs.length > 0 ? (
                  recentLogs.map((log) => (
                    <tr key={log.id}>
                      <td><code>{log.id}</code></td>
                      <td>{log.timestamp}</td>
                      <td>{log.action}</td>
                      <td>{log.partner}</td>
                      <td>
                        <span className={`status-badge ${log.status.toLowerCase()}`}>
                          {log.status}
                        </span>
                      </td>
                      <td>{log.details}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                      No logs found for the selected date range
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card quick-actions-card">
            <h2 className="card-title">Quick Actions</h2>
            <div className="quick-actions">
              <button className="quick-action-btn">
                <span className="action-icon">➕</span>
                <span>Add New Customer</span>
              </button>
              <button className="quick-action-btn">
                <span className="action-icon">📋</span>
                <span>Review Logs</span>
              </button>
              <button className="quick-action-btn">
                <span className="action-icon">⚠️</span>
                <span>Review Compliance Failures</span>
              </button>
              <button className="quick-action-btn">
                <span className="action-icon">❌</span>
                <span>Review KYC Failures</span>
              </button>
            </div>
          </div>
        </div>
        
        {showDatePicker && (
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            onClose={() => setShowDatePicker(false)}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
